/**
 * Auth Service - Handles authentication API calls to backend
 * Uses Firebase Functions HTTP endpoint for backend authentication
 */

import { getFunctions } from 'firebase/functions';
import { sanitizeEmail } from '../utils/sanitization';
import { validateEmail, validatePassword } from '../utils/validation';

// SECURITY: Rate limiting for authentication requests
class AuthRateLimiter {
  private attempts: Map<string, number[]> = new Map();
  private readonly maxAttempts = 5; // 5 login attempts per minute
  private readonly windowMs = 60000; // 1 minute
  private readonly maxAttemptsPerHour = 20; // 20 login attempts per hour
  private readonly hourlyWindowMs = 3600000; // 1 hour

  isAllowed(key: string): { allowed: boolean; message?: string } {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    
    // Remove old attempts outside the window
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);
    const hourlyAttempts = attempts.filter(time => now - time < this.hourlyWindowMs);
    
    // Check hourly limit first
    if (hourlyAttempts.length >= this.maxAttemptsPerHour) {
      return {
        allowed: false,
        message: 'Too many login attempts in the last hour. Please wait before trying again.'
      };
    }
    
    if (recentAttempts.length >= this.maxAttempts) {
      return {
        allowed: false,
        message: 'Too many login attempts. Please try again later.'
      };
    }
    
    recentAttempts.push(now);
    this.attempts.set(key, recentAttempts);
    return { allowed: true };
  }
}

const authRateLimiter = new AuthRateLimiter();

const getAuthApiUrl = (endpoint: string): string => {
  const functions = getFunctions();
  const projectId = functions.app.options.projectId;
  return `https://us-central1-${projectId}.cloudfunctions.net/authApi/api/auth${endpoint}`;
};

export interface RegisterResponse {
  success: boolean;
  message: string;
  contactType?: 'email' | 'phone';
  maskedContact?: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  customToken?: string;
  userId?: string;
  user?: {
    uid: string;
    fullName: string;
    email?: string;
    phone?: string;
    role: string;
    verified: boolean;
  };
}

/**
 * Login user via backend API (for users with hashed passwords in Firestore)
 */
export const loginWithBackendAPI = async (
  emailOrPhone: string,
  password: string
): Promise<LoginResponse> => {
  // Security: Enhanced input validation
  if (!emailOrPhone || typeof emailOrPhone !== 'string' || emailOrPhone.trim().length === 0) {
    throw new Error('Email/phone is required');
  }

  if (!password || typeof password !== 'string' || password.length < 6) {
    throw new Error('Password must be at least 6 characters long');
  }

  // Security: Sanitize input
  const sanitizedEmailOrPhone = emailOrPhone.trim().slice(0, 254); // Max email length
  if (sanitizedEmailOrPhone.length === 0) {
    throw new Error('Invalid email/phone format');
  }

  // SECURITY: Rate limiting for login attempts
  const rateLimitKey = `login_${sanitizedEmailOrPhone}`;
  const rateLimitResult = authRateLimiter.isAllowed(rateLimitKey);
  if (!rateLimitResult.allowed) {
    throw new Error(rateLimitResult.message || 'Too many login attempts. Please try again later.');
  }

  const url = getAuthApiUrl('/login');
  
  let response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        emailOrPhone: sanitizedEmailOrPhone,
        password: password, // Password is hashed server-side, no need to sanitize here
      }),
    });
  } catch (networkError) {
    // Handle network errors (connection issues, CORS, etc.)
    const errorMessage = networkError instanceof Error ? networkError.message : 'Unknown network error';
    if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
      throw new Error('Network error. Please check your internet connection and try again.');
    } else if (errorMessage.includes('CORS')) {
      throw new Error('Connection error. Please try again or contact support.');
    } else {
      throw new Error(`Network error: ${errorMessage}`);
    }
  }

  let data;
  try {
    data = await response.json();
  } catch (parseError) {
    // Handle JSON parse errors
    const errorMessage = parseError instanceof Error ? parseError.message : 'Unknown parse error';
    // Provide user-friendly error message based on response status
    if (response.status === 400) {
      throw new Error('Invalid request. Please check your credentials and try again.');
    } else if (response.status === 429) {
      throw new Error('Too many login attempts. Please wait a few minutes before trying again.');
    } else if (response.status === 500) {
      throw new Error('Server error. Please try again later.');
    } else if (response.status === 503) {
      throw new Error('Service temporarily unavailable. Please try again later.');
    } else {
      throw new Error(`Failed to login. ${errorMessage}`);
    }
  }

  if (!response.ok) {
    if (response.status === 429) {
      const errorMessage = data.message || 'Too many attempts. Please wait a minute before trying again.';
      const error = new Error(errorMessage);
      (error as any).retryAfter = data.retryAfter || 60;
      (error as any).code = data.code || 'RATE_LIMITED';
      throw error;
    }
    if (response.status === 401) {
      // Check if it's an email verification error
      if (data.code === 'EMAIL_NOT_VERIFIED') {
        const error = new Error(data.message || 'Please verify your email address before signing in. Check your inbox for a verification email.');
        (error as any).code = 'EMAIL_NOT_VERIFIED';
        throw error;
      }
      // Provide more informative error messages based on backend response
      const errorMsg = data.message || '';
      const errorMsgLower = errorMsg.toLowerCase();
      
      if (errorMsgLower.includes('not found') || errorMsgLower.includes('no account') || errorMsgLower.includes('user not found')) {
        throw new Error('No account found with this email address. Please check your email or create a new account.');
      } else if (errorMsgLower.includes('password') && (errorMsgLower.includes('incorrect') || errorMsgLower.includes('wrong') || errorMsgLower.includes('invalid'))) {
        throw new Error('The password you entered is incorrect. Please try again or reset your password.');
      } else if (errorMsgLower.includes('invalid') && (errorMsgLower.includes('credential') || errorMsgLower.includes('email') || errorMsgLower.includes('password'))) {
        throw new Error('The email or password you entered is incorrect. Please check your credentials and try again.');
      } else if (errorMsg && errorMsg.length > 10) {
        // Use backend message if it's informative
        throw new Error(errorMsg);
      } else {
        throw new Error('Invalid credentials. Please check your email and password and try again.');
      }
    }
    if (response.status === 400) {
      // Provide more informative error messages for bad requests
      const errorMsg = data.message || '';
      const errorMsgLower = errorMsg.toLowerCase();
      
      if (errorMsgLower.includes('email') && errorMsgLower.includes('invalid')) {
        throw new Error('Please enter a valid email address.');
      } else if (errorMsgLower.includes('password') && (errorMsgLower.includes('required') || errorMsgLower.includes('missing'))) {
        throw new Error('Password is required. Please enter your password.');
      } else if (errorMsgLower.includes('email') && (errorMsgLower.includes('required') || errorMsgLower.includes('missing'))) {
        throw new Error('Email address is required. Please enter your email.');
      } else if (errorMsg && errorMsg.length > 10) {
        // Use backend message if it's informative
        throw new Error(errorMsg);
      } else {
        throw new Error('Invalid request. Please check your input and try again.');
      }
    }
    throw new Error(data.message || 'Failed to login. Please try again.');
  }

  return data;
};
